import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useBookmarks } from '@/hooks/useBookmarks'
import { useContentBundle } from '@/hooks/useContentBundle'
import { useAppStore } from '@/store/appStore'
import type { ApiBookmark, ContentType } from '@/types/api'

interface ResolvedBookmark {
  bookmark: ApiBookmark
  label: string
  typeLabel: string
  url: string
}

export function BookmarksManager() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { setOverlayPage } = useAppStore()
  const { bookmarks, toggleBookmark } = useBookmarks()
  const { data: bundle, isLoading } = useContentBundle()
  const [confirmRemoveBookmark, setConfirmRemoveBookmark] = useState<ApiBookmark | null>(null)
  const [removing, setRemoving] = useState(false)

  const resolvePracticeAreaName = (contentType: ContentType, entityId: number): string => {
    if (!bundle) return ''
    if (contentType === 'PracticeArea') {
      return bundle.practiceAreas.find((pa) => pa.id === entityId)?.displayName ?? ''
    }
    if (contentType === 'Practice') {
      const practice = bundle.practices.find((p) => p.id === entityId)
      return bundle.practiceAreas.find((pa) => pa.id === practice?.practiceAreaId)?.displayName ?? ''
    }
    return ''
  }

  const resolveLabel = (contentType: ContentType, entityId: number): string => {
    if (!bundle) return `#${entityId}`
    if (contentType === 'PracticeArea') {
      return bundle.practiceAreas.find((pa) => pa.id === entityId)?.displayName ?? `PA #${entityId}`
    }
    if (contentType === 'Practice') {
      return bundle.practices.find((p) => p.id === entityId)?.displayName ?? `Practice #${entityId}`
    }
    return `${contentType} #${entityId}`
  }

  const resolveUrl = (contentType: ContentType, entityId: number): string => {
    if (contentType === 'PracticeArea') return `/practice-area/${entityId}`
    if (contentType === 'Practice') return `/practice/${entityId}`
    return '/'
  }

  const resolveTypeLabel = (contentType: ContentType): string => {
    if (contentType === 'PracticeArea') return t('bookmarksManager.typeLabel_PracticeArea')
    if (contentType === 'Practice') return t('bookmarksManager.typeLabel_Practice')
    return contentType
  }

  const resolved = useMemo<ResolvedBookmark[]>(
    () =>
      bookmarks
        .map((bm) => ({
          bookmark: bm,
          label: resolveLabel(bm.contentType, bm.entityId),
          typeLabel: resolveTypeLabel(bm.contentType),
          url: resolveUrl(bm.contentType, bm.entityId),
        }))
        .sort((a, b) =>
          resolvePracticeAreaName(a.bookmark.contentType, a.bookmark.entityId).localeCompare(
            resolvePracticeAreaName(b.bookmark.contentType, b.bookmark.entityId)
          )
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookmarks, bundle]
  )

  const handleNavigate = (bm: ResolvedBookmark) => {
    setOverlayPage(null)
    navigate(bm.url)
  }

  const handleRemoveClick = (bookmark: ApiBookmark) => {
    setConfirmRemoveBookmark(bookmark)
  }

  const handleConfirmRemove = async () => {
    if (!confirmRemoveBookmark) return
    setRemoving(true)
    try {
      await toggleBookmark(confirmRemoveBookmark.contentType, confirmRemoveBookmark.entityId)
      setConfirmRemoveBookmark(null)
    } finally {
      setRemoving(false)
    }
  }

  const handleCancelRemove = () => {
    setConfirmRemoveBookmark(null)
  }

  if (isLoading) {
    return (
      <Box sx={{ pt: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('pages.loading')}</Typography>
      </Box>
    )
  }

  if (resolved.length === 0) {
    return (
      <Box sx={{ pt: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">{t('bookmarksManager.noBookmarksYet')}</Typography>
      </Box>
    )
  }

  return (
    <>
      <List disablePadding>
        {resolved.map((item, index) => (
          <Box key={item.bookmark.id}>
            <ListItem
              disableGutters
              sx={{ py: 1.25, alignItems: 'flex-start' }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pr: 6 }}>
                    <Chip
                      label={item.typeLabel}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ flexShrink: 0 }}
                    />
                    <Typography variant="body1" fontWeight={500} component="span">
                      {item.label}
                    </Typography>
                  </Box>
                }
                secondary={
                  <Button
                    size="small"
                    variant="text"
                    endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => handleNavigate(item)}
                    sx={{ mt: 0.5, p: 0, minWidth: 0 }}
                  >
                    {item.bookmark.contentType === 'PracticeArea'
                      ? t('bookmarksManager.navigateToPracticeArea')
                      : t('bookmarksManager.navigateToPractice')}
                  </Button>
                }
              />
              <ListItemSecondaryAction>
                <Tooltip title={t('bookmarksManager.removeBookmark')}>
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label={t('bookmarksManager.removeBookmark')}
                    onClick={() => handleRemoveClick(item.bookmark)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
            {index < resolved.length - 1 && <Divider />}
          </Box>
        ))}
      </List>

      {/* Remove confirmation dialog */}
      <Dialog open={confirmRemoveBookmark !== null} onClose={handleCancelRemove}>
        <DialogTitle>{t('bookmarksManager.confirmDeleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('bookmarksManager.confirmDeleteMessage')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRemove} disabled={removing}>
            {t('bookmarksManager.cancel')}
          </Button>
          <Button
            onClick={handleConfirmRemove}
            color="error"
            variant="contained"
            disabled={removing}
            startIcon={removing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {removing ? t('bookmarksManager.removing') : t('bookmarksManager.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
